"use client";

import { Line, LineChart, CartesianGrid, XAxis, YAxis } from "recharts";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { Skeleton } from "@/components/ui/skeleton";

interface YearlyComparisonItem {
  tahun: string;
  total_pendaftar: number;
  total_validasi: number;
  total_lulus: number;
  total_daftar_kembali: number;
  total_isi_ukt: number;
  total_valid_isi_ukt: number;
  total_bayar_ukt: number;
}

interface ComparisonYearlyLineChartProps {
  data: YearlyComparisonItem[] | undefined;
  isLoading: boolean;
}

const chartConfig = {
  total_pendaftar: {
    label: "Pendaftar",
    color: "hsl(var(--chart-1))",
  },
  total_lulus: {
    label: "Lulus",
    color: "hsl(var(--chart-2))",
  },
  total_valid_isi_ukt: {
    label: "Valid Isi UKT",
    color: "hsl(var(--chart-4))",
  },
  total_daftar_kembali: {
    label: "Daftar Kembali",
    color: "hsl(var(--chart-5))",
  },
} satisfies ChartConfig;

export function ComparisonYearlyLineChart({
  data,
  isLoading,
}: ComparisonYearlyLineChartProps) {
  if (isLoading) {
    return (
      <Card className="flex flex-col h-full min-h-[400px]">
        <CardHeader>
          <Skeleton className="h-6 w-1/2 mb-2" />
          <Skeleton className="h-4 w-1/3" />
        </CardHeader>
        <CardContent className="flex-1">
          <Skeleton className="h-full w-full rounded-xl" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="flex flex-col h-full">
      <CardHeader>
        <CardTitle>Tren Tahunan</CardTitle>
        <CardDescription>
          Tren pertumbuhan pendaftaran dari tahun ke tahun.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex-1 pb-4">
        <ChartContainer config={chartConfig} className="min-h-[350px] w-full">
          <LineChart
            accessibilityLayer
            data={data}
            margin={{
              top: 20,
              right: 20,
              left: 0,
              bottom: 0,
            }}
          >
            <CartesianGrid vertical={false} strokeDasharray="3 3" />
            <XAxis
              dataKey="tahun"
              tickLine={false}
              tickMargin={10}
              axisLine={false}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tickMargin={10}
              fontSize={12}
            />
            <ChartTooltip
              cursor={false}
              content={<ChartTooltipContent indicator="line" />}
            />
            <ChartLegend content={<ChartLegendContent />} />
            <Line
              type="monotone"
              dataKey="total_pendaftar"
              stroke="var(--color-total_pendaftar)"
              strokeWidth={2}
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
            />
            <Line
              type="monotone"
              dataKey="total_lulus"
              stroke="var(--color-total_lulus)"
              strokeWidth={2}
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
            />
            <Line
              type="monotone"
              dataKey="total_valid_isi_ukt"
              stroke="var(--color-total_valid_isi_ukt)"
              strokeWidth={2}
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
            />
            <Line
              type="monotone"
              dataKey="total_daftar_kembali"
              stroke="var(--color-total_daftar_kembali)"
              strokeWidth={2}
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}
